import { useRollbar } from '@rollbar/react'
import {
  fetchPageDataFailure,
  fetchPageDataSuccess,
} from 'client/actions/pageActions'
import { fetchEditorPage } from 'client/api/pageApi'
import usePresentPage from 'client/hooks/usePresentPage'
import { selectors } from 'client/reducers/pageReducer'
import { useWebsiteSettings } from 'client/store'
import { NetworkError, NotFound } from 'common/errors'
import { AccessDenied } from 'common/errors/AccessDenied'
import { useActions } from 'common/hooks/useActions'
import { changeWebsitePageId, getUrlPathList } from 'common/utils/urlUtils'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCurrentUrl } from 'src/editor/hooks/use-current-url'
import { UnsavedPageModal } from 'src/editor/layout/sidebar/pages-management/components/unsaved-page-modal'
import { getWebsiteLayoutPageId } from 'src/editor/store/website-settings/website-settings-selector'

export const useFetchAndSwitchPage = () => {
  const { t } = useTranslation()
  const rollbar = useRollbar()
  const [isLoading, setIsLoading] = useState(false)
  const [unsavedPageId, setUnsavedPageId] = useState<null | number>(null)
  const layoutPageId = useWebsiteSettings(getWebsiteLayoutPageId)
  const dispatchFetchPageDataFail = useActions(fetchPageDataFailure)
  const dispatchFetchPageData = useActions(fetchPageDataSuccess)

  const isPageModified = usePresentPage(selectors.isPageModified)

  const currentUrl = useCurrentUrl()
  const urlPageId = getUrlPathList(currentUrl)[3]

  // the second argument is a hack that allows to bypass REDUX value
  // todo refactor in #SC-83061
  const fetchPageData = async (
    pageId: number,
    bypassedLayoutPageId?: number,
  ) => {
    changeWebsitePageId(pageId)
    setIsLoading(true)
    try {
      const mergedWebsitePage = await fetchEditorPage(
        pageId,
        bypassedLayoutPageId || layoutPageId,
      )

      dispatchFetchPageData(mergedWebsitePage, rollbar)
      setIsLoading(false)
    } catch (error) {
      if (error instanceof AccessDenied) {
        // do nothing
      } else if (error instanceof NotFound) {
        dispatchFetchPageDataFail(t('core.errors.page_not_found'))
      } else if (error instanceof NetworkError) {
        dispatchFetchPageDataFail(t('core.errors.no_connection'))
      } else {
        dispatchFetchPageDataFail(t('core.errors.went_wrong_contact_support'))
      }
      setIsLoading(false)
    }
  }
  const checkPageModification = (pageId: number, layoutPageId: number) => {
    if (isPageModified) {
      setUnsavedPageId(pageId)
    } else {
      fetchPageData(pageId, layoutPageId)
    }
  }
  const renderUnsavedPageModal = () =>
    unsavedPageId && (
      <UnsavedPageModal
        isVisible={!!unsavedPageId}
        onClose={() => setUnsavedPageId(null)}
        fetchPageData={() => fetchPageData(unsavedPageId as number)}
      />
    )

  return {
    urlPageId,
    isLoading,
    fetchPageData,
    checkPageModification,
    renderUnsavedPageModal,
  }
}
